import React from 'react'
import {
  TextField,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  Box
} from '@mui/material'
import { Address } from 'hooks/usePayment'
import AddressFields from 'components/AddressFields'
import NameFields from 'components/NameFields'
import { useTranslation } from 'react-i18next'

type NewCardFormProps = {
  formValues: {
    cardNumber: string
    expirationDate: string
    cvv: string
    firstName: string
    lastName: string
    addressLine1: string
    addressLine2: string
    city: string
    state: string
    zipCode: string
  }
  errors: Record<string, string>
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  handleSelectChange: (e: any) => void
  useMyAddressChecked: boolean
  handleUseMyAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  saveForNextTime: boolean
  handleSaveForNextTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  addresses: Address[]
  selectedAddressId: string | null
  handleAddressSelect: (addressId: string) => void
}

const AddNewCardForm: React.FC<NewCardFormProps> = ({
  formValues,
  errors,
  handleInputChange,
  handleBlur,
  handleSelectChange,
  useMyAddressChecked,
  handleUseMyAddressChange,
  saveForNextTime,
  handleSaveForNextTimeChange,
  addresses,
  selectedAddressId,
  handleAddressSelect
}) => {
  const { t } = useTranslation()

  return (
    <>
      <Divider
        sx={{
          width: '100%',
          borderColor: '#E0E0E0',
          mb: '14px'
        }}
      />
      <Typography variant='h3'>
        {t('checkout.cardForm.cardInformation')}
      </Typography>

      <TextField
        label={t('checkout.cardForm.cardNumber')}
        variant='outlined'
        required
        fullWidth
        margin='dense'
        name='cardNumber'
        value={formValues.cardNumber}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={Boolean(errors.cardNumber)}
        helperText={errors.cardNumber}
        inputProps={{ maxLength: 19 }}
      />

      <Box display='flex' gap={2}>
        <TextField
          label={t('checkout.cardForm.expirationDate')}
          variant='outlined'
          required
          fullWidth
          margin='dense'
          name='expirationDate'
          value={formValues.expirationDate}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder='MM/YY'
          error={Boolean(errors.expirationDate)}
          helperText={errors.expirationDate}
        />

        <TextField
          label={t('checkout.cardForm.cvv')}
          variant='outlined'
          required
          fullWidth
          margin='dense'
          name='cvv'
          value={formValues.cvv}
          onChange={handleInputChange}
          onBlur={handleBlur}
          error={Boolean(errors.cvv)}
          helperText={errors.cvv}
          inputProps={{ maxLength: 3 }}
        />
      </Box>

      <Typography variant='h3'>
        {t('checkout.cardForm.billingAddress')}
      </Typography>

      {!useMyAddressChecked && (
        <>
          <NameFields
            formValues={{
              firstName: formValues.firstName,
              lastName: formValues.lastName
            }}
            errors={{
              firstName: errors.firstName,
              lastName: errors.lastName
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            size={'medium'}
          />

          <AddressFields
            formValues={{
              addressLine1: formValues.addressLine1,
              addressLine2: formValues.addressLine2,
              city: formValues.city,
              state: formValues.state,
              zipCode: formValues.zipCode
            }}
            errors={{
              addressLine1: errors.addressLine1,
              city: errors.city,
              state: errors.state,
              zipCode: errors.zipCode
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            handleSelectChange={handleSelectChange}
            size={'medium'}
          />
        </>
      )}

      {useMyAddressChecked && (
        <>
          {addresses.map(address => (
            <Card
              variant='outlined'
              key={address.id}
              sx={{
                my: 1,
                p: 2,
                cursor: 'pointer',
                borderWidth: 2,
                borderColor:
                  selectedAddressId === address.id ? 'primary.main' : '#E0E0E0',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => handleAddressSelect(address.id)}
            >
              <Typography variant='body1'>{address.customerName}</Typography>
              <Typography variant='body2'>
                {address.addressLine1}
                {address.addressLine2 && `, ${address.addressLine2}`},{' '}
                {address.city}, {address.state} {address.zipCode}
              </Typography>
            </Card>
          ))}
        </>
      )}

      <FormGroup sx={{ color: '#8F8F8F' }}>
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main'
                }
              }}
              checked={useMyAddressChecked}
              onChange={handleUseMyAddressChange}
            />
          }
          label={t('checkout.cardForm.useMyAddress')}
        />
      </FormGroup>

      <FormGroup sx={{ color: '#8F8F8F' }}>
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main'
                }
              }}
              checked={saveForNextTime}
              onChange={handleSaveForNextTimeChange}
            />
          }
          label={t('checkout.cardForm.saveForNextTime')}
        />
      </FormGroup>
      <Divider
        sx={{
          width: '100%',
          borderColor: '#E0E0E0',
          marginTop: '14px'
        }}
      />
    </>
  )
}

export default AddNewCardForm
